import {
    MoneyValue,
    Tabs,
    formikHelpers,
    lucideIcons,
    toast,
    ComboboxCountryItem,
    ComboboxCountryValue,
    InputGroup,
    Badge,
} from '@round/creatorbase-ui';
import { creatorbase } from '@round/api';
import { FormikProvider, useFormik } from 'formik';
import { InfluencerLocationOption, makeLocationOption } from 'Hooks/selectOptions/useInfluencerLocationSelect';
import { ValueType } from 'react-select';
import { GenericDropdownOption } from 'App.types';
import { mapClientPayeeToOption, PayeeOption } from 'Modules/Plans/Finance/Payee/hooks/usePayeeSelect';
import { usePayeeCombobox } from 'Modules/Plans/Finance/Payee/hooks/usePayeeCombobox';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { useInfluencerLocations } from 'Hooks/useInfluencerLocations';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import compact from 'lodash/compact';
import { getObjectDifference, mapContentTagsToOptions } from 'helpers';
import styles from './ClientInfluencerForm.module.css';
import { onKey } from 'utility/events';
import cn from 'classnames';
import {
    useClientContentTags,
    useClientInfluencer,
    useClientPayees,
    useCreatorbaseCurrencies,
    useInvalidateClientInfluencer,
    useUpdateClientInfluencer,
} from '@round/creatorbase-helpers/api-hooks';
import { paymentProviderOptions, PlatformIcon } from 'Modules/Plans/helpers';
import PaymentProvider from 'Modules/Plans/components/PaymentProvider/PaymentProvider';

const { Field, InputField, ComboboxField, MoneyInputField, ComboboxExternalField, TextareaField } = formikHelpers;
const { UserIcon, CreditCardIcon, MessageSquareIcon, SearchIcon } = lucideIcons;

const isPayeeOptionEqualById = (a: PayeeOption, b: PayeeOption) => a.value === b.value;

type FormValues = {
    location: ValueType<InfluencerLocationOption, false>;
    contact_details: string;
    content_tags: ValueType<GenericDropdownOption<number>, true>;
    tiktok_cost: MoneyValue;
    instagram_cost: MoneyValue;
    payees: ValueType<PayeeOption, true>;
    paypal_email: string;
    preferred_payment_provider: ValueType<GenericDropdownOption<creatorbase.PaymentProvider>, false>;
    notes: string;
};

type Props = {
    clientInfluencerId: string | null | undefined;
    isLoading?: boolean;
    className?: string;
    onUpdate?: () => void;
};

const ClientInfluencerForm = ({ clientInfluencerId, isLoading = false, className, onUpdate }: Props) => {
    const { data: clientInfluencer } = useClientInfluencer(clientInfluencerId, {
        enabled: !isLoading,
    });
    const { update: updateClientInfluencer, isLoading: isUpdateLoading } = useUpdateClientInfluencer();
    const invalidateClientInfluencer = useInvalidateClientInfluencer();

    const { selectedClientId } = useClientSelect();
    const { locations: locationTags, isLoading: locationsLoading } = useInfluencerLocations();
    const locationOptions = locationTags.map(makeLocationOption);
    const { currencyOptions, getCurrencyOptionsById } = useCreatorbaseCurrencies();
    const { tags: contentTags, isLoading: contentTagsLoading } = useClientContentTags(selectedClientId);
    const contentTagsOptions = useMemo(() => mapContentTagsToOptions(contentTags), [contentTags]);
    const { data: accountPayees } = useClientPayees(
        {
            client_id: selectedClientId,
            id: clientInfluencer?.payee_ids?.join(','),
            page_size: clientInfluencer?.payee_ids?.length ?? 0,
        },
        { enabled: !!clientInfluencer?.payee_ids?.length, placeholderData: keepPreviousData }
    );

    const selectedPayeeOptions = useMemo(
        () => accountPayees?.results.map(mapClientPayeeToOption) ?? [],
        [accountPayees]
    );

    const payeeCombobox = usePayeeCombobox({
        params: { client_id: selectedClientId, is_payable: true },
        enabled: Boolean(selectedClientId),
    });

    const formik = useFormik<FormValues>({
        initialValues: {
            contact_details: clientInfluencer?.contact_details ?? '',
            notes: clientInfluencer?.notes ?? '',
            tiktok_cost: {
                value: clientInfluencer?.tiktok_cost?.toString() ?? '',
                currency: getCurrencyOptionsById(clientInfluencer?.tiktok_currency_id) ?? null,
            },
            instagram_cost: {
                value: clientInfluencer?.instagram_cost?.toString() ?? '',
                currency: getCurrencyOptionsById(clientInfluencer?.instagram_currency_id) ?? null,
            },
            paypal_email: clientInfluencer?.paypal_email ?? '',
            content_tags: compact(
                clientInfluencer?.content_tags.map((tagId) =>
                    contentTagsOptions.find((option) => option.value === tagId)
                )
            ),
            location: locationOptions.find((option) => option.value === clientInfluencer?.location_id) ?? null,
            payees: selectedPayeeOptions,
            preferred_payment_provider:
                paymentProviderOptions.find(
                    (option) => option.value === clientInfluencer?.preferred_payment_provider
                ) ?? null,
        },
        enableReinitialize: true,
        onSubmit: (values, { setSubmitting }) => {
            if (!clientInfluencer?.id) {
                return;
            }

            const mapToApi = (v: typeof values): Partial<creatorbase.PatchClientInfluencerData> => ({
                contact_details: v.contact_details,
                notes: v.notes,
                content_tags: v.content_tags?.map((t) => t.value),
                location_id: v.location?.value ?? null,
                paypal_email: v.paypal_email || '',
                payee_ids: v.payees?.map((p) => p.value),
                tiktok_cost:
                    String(v.tiktok_cost.value).length && Number.isFinite(Number(v.tiktok_cost.value))
                        ? Number(v.tiktok_cost.value)
                        : null,
                tiktok_currency_id: v.tiktok_cost.currency?.value ?? null,
                instagram_cost:
                    String(v.instagram_cost.value).length && Number.isFinite(Number(v.instagram_cost.value))
                        ? Number(v.instagram_cost.value)
                        : null,
                instagram_currency_id: v.instagram_cost.currency?.value ?? null,
                preferred_payment_provider: v.preferred_payment_provider?.value ?? undefined,
            });

            const updatedValues = getObjectDifference(mapToApi(values), mapToApi(formik.initialValues));
            if (!Object.keys(updatedValues).length) {
                return;
            }

            setSubmitting(true);
            updateClientInfluencer(
                { id: clientInfluencer.id, ...updatedValues },
                {
                    onSuccess: (data) => {
                        invalidateClientInfluencer(data.id);
                        toast.info('Updated');
                        onUpdate?.();
                    },
                    onError: () => {
                        toast.error('Could not update influencer');
                    },
                    onSettled: () => {
                        setSubmitting(false);
                    },
                }
            );
        },
    });

    const hasNotesSet = formik.values.notes?.length > 0;

    return (
        <Tabs orientation="vertical" defaultValue="creator-info" className={cn(styles.tabs, className)}>
            <Tabs.List className={styles.tabList}>
                <Tabs.Trigger value="creator-info" className={styles.tabListTrigger}>
                    <UserIcon className={styles.tabListIcon} />
                    Creator Info
                </Tabs.Trigger>
                <Tabs.Trigger value="payment-info" className={styles.tabListTrigger}>
                    <CreditCardIcon className={styles.tabListIcon} />
                    Payment Info
                </Tabs.Trigger>
                <Tabs.Trigger value="notes" className={styles.tabListTrigger}>
                    <MessageSquareIcon
                        className={cn(styles.tabListIcon, { [styles.tabListIconNotesSet]: hasNotesSet })}
                    />
                    Notes
                    {hasNotesSet && (
                        <Badge variant="secondary" size="sm" className={styles.notesSetBadge}>
                            ...
                        </Badge>
                    )}
                </Tabs.Trigger>
            </Tabs.List>

            <FormikProvider value={formik}>
                <Tabs.Content value="creator-info" className={styles.formSection}>
                    <Field name="location" className={styles.location}>
                        <Field.Label htmlFor="location" className={styles.label}>
                            Location
                        </Field.Label>
                        <ComboboxField
                            name="location"
                            isClearable={false}
                            disabled={locationsLoading || isLoading || isUpdateLoading}
                            items={locationOptions}
                            placeholder="Select location"
                            onBlur={formik.submitForm}
                            onValueChange={formik.submitForm}
                            renderItem={(item: InfluencerLocationOption) => <ComboboxCountryItem item={item} />}
                            renderValue={(item: InfluencerLocationOption) => <ComboboxCountryValue item={item} />}
                        />
                        <Field.Error name="location" />
                    </Field>

                    <Field name="contact_details" className={styles.contactDetails}>
                        <Field.Label htmlFor="contact_details" className={styles.label}>
                            Contact Details
                        </Field.Label>
                        <InputField
                            name="contact_details"
                            placeholder="Enter contact details"
                            disabled={isLoading || isUpdateLoading}
                            onBlur={formik.submitForm}
                            onKeyDown={onKey('Enter', formik.submitForm)}
                        />
                        <Field.Error name="contact_details" />
                    </Field>

                    <Field name="content_tags" className={styles.contentTags}>
                        <Field.Label htmlFor="content_tags" className={styles.label}>
                            Content Tags
                        </Field.Label>
                        <ComboboxExternalField
                            name="content_tags"
                            disabled={contentTagsLoading || isLoading || isUpdateLoading}
                            multiple
                            items={contentTagsOptions}
                            placeholder="Search tags..."
                            onBlur={formik.submitForm}
                            onValueChange={formik.submitForm}
                        >
                            <InputGroup.Addon align="inline-start">
                                <SearchIcon />
                            </InputGroup.Addon>
                        </ComboboxExternalField>
                        <Field.Error name="content_tags" />
                    </Field>
                </Tabs.Content>

                <Tabs.Content value="payment-info" className={styles.formSection}>
                    <Field name="tiktok_cost" className={styles.tiktokCost}>
                        <Field.Label htmlFor="tiktok_cost" className={styles.label}>
                            <PlatformIcon platform="tiktok" /> TikTok
                        </Field.Label>
                        <MoneyInputField
                            name="tiktok_cost"
                            disabled={isUpdateLoading || isLoading}
                            currencies={currencyOptions}
                            onBlur={formik.submitForm}
                            onKeyDown={onKey('Enter', formik.submitForm)}
                            onChange={(value: MoneyValue) => {
                                if (value.currency?.value !== formik.values.tiktok_cost.currency?.value) {
                                    formik.submitForm();
                                }
                            }}
                        />
                        <Field.Error name="tiktok_cost" />
                    </Field>

                    <Field name="instagram_cost" className={styles.instagramCost}>
                        <Field.Label htmlFor="instagram_cost" className={styles.label}>
                            <PlatformIcon platform="instagram" /> Instagram
                        </Field.Label>
                        <MoneyInputField
                            name="instagram_cost"
                            disabled={isUpdateLoading || isLoading}
                            currencies={currencyOptions}
                            onBlur={formik.submitForm}
                            onKeyDown={onKey('Enter', formik.submitForm)}
                            onChange={(value: MoneyValue) => {
                                if (value.currency?.value !== formik.values.instagram_cost.currency?.value) {
                                    formik.submitForm();
                                }
                            }}
                        />
                        <Field.Error name="instagram_cost" />
                    </Field>

                    <Field name="preferred_payment_provider" className={styles.preferredPaymentProvider}>
                        <Field.Label htmlFor="preferred_payment_provider" className={styles.label}>
                            Preferred Payment Provider
                        </Field.Label>

                        <ComboboxField
                            name="preferred_payment_provider"
                            disabled={isLoading || isUpdateLoading}
                            items={paymentProviderOptions}
                            onBlur={formik.submitForm}
                            onValueChange={formik.submitForm}
                            renderValue={(item: GenericDropdownOption<creatorbase.PaymentProvider>) => (
                                <PaymentProvider paymentProvider={item.value} />
                            )}
                            renderItem={(item: GenericDropdownOption<creatorbase.PaymentProvider>) => (
                                <PaymentProvider paymentProvider={item.value} />
                            )}
                        />

                        <Field.Error name="preferred_payment_provider" />
                    </Field>

                    <Field name="paypal_email" className={styles.paypalEmail}>
                        <Field.Label htmlFor="paypal_email" className={styles.label}>
                            PayPal Email
                        </Field.Label>
                        <InputField
                            name="paypal_email"
                            placeholder="Enter paypal email"
                            disabled={isLoading || isUpdateLoading}
                            onBlur={formik.submitForm}
                            onKeyDown={onKey('Enter', formik.submitForm)}
                        />
                        <Field.Error name="paypal_email" />
                    </Field>

                    <Field name="payees" className={styles.payees}>
                        <Field.Label htmlFor="payees" className={styles.label}>
                            Payees
                        </Field.Label>
                        <ComboboxField
                            name="payees"
                            multiple
                            disabled={isLoading || isUpdateLoading}
                            items={payeeCombobox.items}
                            placeholder="Search payees..."
                            filter={payeeCombobox.filter}
                            isItemEqualToValue={isPayeeOptionEqualById}
                            onInputValueChange={payeeCombobox.onInputValueChange}
                            onItemsListScroll={payeeCombobox.onItemsListScroll}
                            onBlur={formik.submitForm}
                            onValueChange={formik.submitForm}
                        />
                        <Field.Error name="payees" />
                    </Field>
                </Tabs.Content>

                <Tabs.Content value="notes" className={styles.formSection}>
                    <Field name="notes" className={styles.notes}>
                        <Field.Label htmlFor="notes" className={styles.label}>
                            Notes
                        </Field.Label>
                        <TextareaField
                            name="notes"
                            placeholder="Enter notes"
                            disabled={isLoading || isUpdateLoading}
                            onBlur={formik.submitForm}
                            onKeyDown={onKey('Enter', formik.submitForm)}
                        />
                        <Field.Error name="notes" />
                    </Field>
                </Tabs.Content>
            </FormikProvider>
        </Tabs>
    );
};

export default ClientInfluencerForm;
